import { CANCELED_API_REQ } from '@commutifi-fe/constants';
import { ApiError, CommutifiErrorData, NetworkError } from '@commutifi-fe/interfaces';
import { delay } from '@commutifi-fe/utils';
import Axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse, CreateAxiosDefaults } from 'axios';
import { IApiClient } from '../Domain/Repositories/Http';
export const queryParamsSerializer = (params: any) => Object.keys(params).filter(k => params[k] !== undefined).map(k => {
  // dark-vador convention: null values use "null:<field>" prefix for IS NULL filtering
  if (params[k] === null) {
    return `null:${encodeURIComponent(k)}`;
  }
  return `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`;
}).join('&');

/**
 * Retry delay for invalid response errors (e.g., during deployments)
 * Using 1 second to allow load balancer to route to healthy pods
 */
const INVALID_RESPONSE_RETRY_DELAY_MS = 1000;
type ApiInstanceOptions = AxiosRequestConfig & {
  queryParams?: object;
  _isInvalidResponseRetry?: boolean;
};
export function apiInstance(api: AxiosInstance, {
  onError,
  onSuccess
}: {
  onError?: (error: AxiosError<CommutifiErrorData>) => void | typeof CANCELED_API_REQ;
  onSuccess?: (res: AxiosResponse) => void;
} = {}) {
  return async function axiosApi<ApiResponse = any>(url: string, options: ApiInstanceOptions = {}): Promise<ApiResponse> {
    // Support legacy code where we made queryParams option available
    if (options.queryParams) {
      options.params = options.queryParams;
    }

    // Filter out undefined query params
    if (options.params) {
      options.params = Object.fromEntries(Object.entries(options.params).filter(([, v]) => v !== undefined));
    }
    return api(url, options).then(async res => {
      onSuccess?.(res);

      // Validate response data exists - network issues can cause undefined data with 200 status
      // This can happen during deployments when load balancer routes to terminating pods,
      // or on mobile devices with interrupted connections
      if (res.data === undefined) {
        // Retry once with delay to allow load balancer to route to healthy pods
        if (!options._isInvalidResponseRetry) {
          await delay(INVALID_RESPONSE_RETRY_DELAY_MS);
          return axiosApi<ApiResponse>(url, {
            ...options,
            _isInvalidResponseRetry: true
          });
        }
        const error = new Error(`API response data is undefined for ${options.method?.toUpperCase() || 'GET'} ${url}`);
        error.name = 'InvalidResponseError';
        throw error;
      }
      return res.data;
    }).catch((error: AxiosError<CommutifiErrorData>) => {
      const err = onError?.(error);
      if (err === CANCELED_API_REQ) {
        return err;
      }
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        // Throw a proper ApiError instead of raw response object for better Sentry tracking
        throw new ApiError(error.response);
      }
      if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        // Throw a proper NetworkError instead of raw XMLHttpRequest for better Sentry tracking
        throw new NetworkError(error.config?.url);
      }
      // Something happened in setting up the request that triggered an Error
      throw error;
    });
  };
}
export class AxiosHttpClient implements IApiClient {
  private client: AxiosInstance;
  public getClient() {
    return this.client;
  }
  protected createAxiosClient(config: CreateAxiosDefaults): AxiosInstance {
    return Axios.create({
      responseType: 'json' as const,
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 10 * 1000,
      ...config
    });
  }
  constructor(config: CreateAxiosDefaults) {
    this.client = this.createAxiosClient(config);
  }
  async post<TBody, TResponse>(path: string, body: TBody, config?: AxiosRequestConfig<TBody>): Promise<TResponse> {
    return apiInstance(this.client)<TResponse>(path, {
      method: 'post',
      data: body,
      ...config
    });
  }
  async patch<TResponse, TBody>(path: string, payload: TBody, config?: AxiosRequestConfig<TBody>): Promise<TResponse> {
    return apiInstance(this.client)<TResponse>(path, {
      method: 'patch',
      data: payload,
      ...config
    });
  }
  async put<TResponse, TBody>(path: string, payload: TBody, config?: AxiosRequestConfig<TBody>): Promise<TResponse> {
    return apiInstance(this.client)<TResponse>(path, {
      method: 'put',
      data: payload,
      ...config
    });
  }
  async get<TResponse>(path: string, config?: AxiosRequestConfig): Promise<TResponse> {
    return apiInstance(this.client)<TResponse>(path, {
      method: 'get',
      ...config
    });
  }
}